import {Collapse, css, useTheme} from '@mui/material';
import {
  ArrayUtils,
  ConfigObjectType,
  DlMuiComponentBox,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightIconButton,
  LightSplitLayout,
  LoadingSpinner,
  omitNullsy,
  serializeJSONToString,
  useApiQuery,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  Formatters,
  HaidiSchema,
  HealthcareTableDetailFormatter,
  HealthcareTableEventLinkFormatter,
  LTColumn,
  FILTER_ACTIVE_ICON_COLORS,
  LightDateRangeFilter,
  LightSavedFiltersButton,
  LightSelectFilter,
  LightSelectedFilters,
  LightTable,
  PatientEncounterDocumentBreadcrumbs,
  DynamicChunk,
  formatPatientBreadcrumb,
  getEncounterEventGroupKey,
  patientContainer,
  useAddDynamicItem,
  useFormatters,
  useSnomedLabels,
} from '@wallaby-ui/document';
import classNames from 'classnames';
import {isEqual} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useParams} from 'react-router-dom';

import stylesLayout from './AppLayout.less';
import {useStandardTypes} from './encounter/hai/useStandardTypes';

type RowType = HaidiSchema.components['schemas']['PatientEventDto'];
interface OwnProps {
  config: ConfigObjectType;
}
type Props = OwnProps;

const DEFAULT_PATIENT_TIMELINE_FILTERS = {};
const DEFAULT_PATIENT_TIMELINE_SORTING = {
  field: 'date' as string,
  direction: 'desc',
};
export const PatientLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const {patientId} = useParams();

  const storageIdPrefix = `/patients/${patientId}`;
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters = useMemo(() => {
    return {
      timelineOpen: query.timelineOpen ?? false,
      timeline: query.timeline ?? DEFAULT_PATIENT_TIMELINE_FILTERS,
      timelineSorting: query.timelineSorting ?? DEFAULT_PATIENT_TIMELINE_SORTING,
    };
  }, [query]);

  const patientUrl = config.service.url.patientDetail.replace('{patientId}', patientId);
  const patientParams: HaidiSchema.components['schemas']['HealthcareTimelineFilterDto'] = omitNullsy({
    startDate: filters.timeline.startDate,
    endDate: filters.timeline.endDate,
    eventTypes: filters.timeline.eventTypes,
  });
  const {
    data,
    isLoading,
    isError: _,
  } = useApiQuery<HaidiSchema.components['schemas']['PatientDetailDto']>([patientUrl, serializeJSONToString(patientParams)], null, {
    axiosConfig: {
      url: patientUrl,
      params: new URLSearchParams(patientParams as any),
    },
  });

  const {addDynamicItem} = useAddDynamicItem();
  useEffect(() => {
    if (data && data.patient && patientId) {
      const label = formatPatientBreadcrumb({
        isLoading,
        id: patientId,
        name: data.patient.fullName,
        birthNumber: data.patient.identificationNumber,
      });
      addDynamicItem({
        isLoading,
        routeId: 'patient',
        id: patientId,
        paramsString: `/${patientId}`,
        label,
        isPatient: true,
        cleanLocalStorage: [storageIdPrefix],
      });
    }
  }, [addDynamicItem, isLoading, data, patientId, storageIdPrefix]);

  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const formatters = useFormatters();
  const {t, parseDate, formatString} = formatters;
  const sortTimeDirection = filters.timelineSorting.direction;
  const {getTranslationForStandardTypeId, getCodeLabelForStandardTypeId} = useStandardTypes({config});
  const {snomedLabels, snomedLabelsLoading} = useSnomedLabels(config);
  const snomedRef = React.useRef({snomedLabels: {}, snomedLabelsLoading: true});
  snomedRef.current = {snomedLabels, snomedLabelsLoading};
  const timelineColumns = useMemo(() => {
    return getTimelineColumns(
      formatters,
      sortTimeDirection,
      patientId,
      snomedRef,
      getTranslationForStandardTypeId,
      getCodeLabelForStandardTypeId
    );
  }, [sortTimeDirection, patientId, snomedRef, formatters, getTranslationForStandardTypeId, getCodeLabelForStandardTypeId]);
  const sortedEvents = useMemo(() => {
    return ArrayUtils.sortedBy(
      data?.events ?? [],
      (v) => {
        const eventTypeKey = getEncounterEventGroupKey(v.eventType);
        switch (filters.timelineSorting.field) {
          case 'date':
            return [parseDate(v.timestamp), eventTypeKey];
          default:
            return [];
        }
      },
      [filters.timelineSorting.direction === 'desc', filters.timelineSorting.direction === 'desc']
    );
  }, [data?.events, parseDate, filters.timelineSorting]);
  const [healthcareDefaultScroll, setHealthcareDefaultScroll] = useLocalStorage(`${storageIdPrefix}.healthcareScroll`, 0);
  const healthcareOnScroll = useCallback(
    (event: Event) => {
      setHealthcareDefaultScroll((event.target as HTMLElement).scrollTop);
    },
    [setHealthcareDefaultScroll]
  );
  const [healthcareDefaultDynamicChunks, setHealthcareDefaultDynamicChunks] = useLocalStorage(
    `${storageIdPrefix}.healthcareDynamicChunks`,
    [] as DynamicChunk[]
  );
  const healthcareDynamicChunksOnChange = useCallback(
    (newDynamicChunks: DynamicChunk[]) => {
      setHealthcareDefaultDynamicChunks(newDynamicChunks);
    },
    [setHealthcareDefaultDynamicChunks]
  );

  // show spinner on initial load
  const [prevPatientId, setPrevPatientId] = useState<string | undefined>(undefined);
  const isInitialLoad = patientId !== prevPatientId;
  useEffect(() => {
    if (patientId !== prevPatientId) {
      setPrevPatientId(patientId);
    }
  }, [patientId, prevPatientId]);
  // if (isError) return <NotFoundComponentBox />;
  return (
    <div key={patientId} className={stylesLayout.dlTabColumnWrapper}>
      <Helmet
        title={formatString(t('patient.detail.pageTitle'), {
          patient: formatPatientBreadcrumb({
            isLoading,
            id: patientId,
            name: data?.patient?.fullName,
            birthNumber: data?.patient?.identificationNumber,
          }),
        })}
      />
      <div css={muiStyles.wrapper}>
        <LightSplitLayout
          initSizes={[70, 30]}
          minSizes={[500, 200]}
          storageId='/patients.PatientLayout.split'
          style={{overflowX: 'hidden', overflow: 'hidden'}}
        >
          <div css={muiStyles.leftColumn}>
            <PatientEncounterDocumentBreadcrumbs
              patient={{
                isLoading,
                id: patientId,
                name: data?.patient.fullName,
                birthNumber: data?.patient.identificationNumber,
              }}
            />
            <div
              data-name='patients-timeline'
              css={muiStyles.leftColumnBottom}
              style={{
                height: `calc(100% - 98px)`,
              }}
            >
              <IconHeaderWithSearch iconName={'documentsIcon'} label={t('patient.detail.timeline')} childrenWrapperStyle={{gap: 6}}>
                <LightSelectedFilters
                  hasFilters={!isEqual(omitNullsy(filters.timeline), omitNullsy(DEFAULT_PATIENT_TIMELINE_FILTERS))}
                  handleResetFilters={() => {
                    changeFilters({
                      timeline: DEFAULT_PATIENT_TIMELINE_FILTERS,
                    });
                    setHealthcareDefaultScroll(0);
                  }}
                  hasSorting={!isEqual(filters.timelineSorting, DEFAULT_PATIENT_TIMELINE_SORTING)}
                  handleResetSorting={() => {
                    changeFilters({
                      timelineSorting: DEFAULT_PATIENT_TIMELINE_SORTING,
                    });
                    setHealthcareDefaultScroll(0);
                  }}
                />
                <LightIconButton
                  iconName='filterIcon'
                  useFixedViewBox='0 0 20 16'
                  style={{width: 20, height: 16}}
                  title={filters.timelineOpen ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
                  onClick={() => changeFilters({timelineOpen: !filters.timelineOpen})}
                  selected={filters.timelineOpen}
                  colors={
                    !isEqual(omitNullsy(filters.timeline), omitNullsy(DEFAULT_PATIENT_TIMELINE_FILTERS)) && !filters.timelineOpen
                      ? FILTER_ACTIVE_ICON_COLORS
                      : undefined
                  }
                />
                <LightSavedFiltersButton
                  storageIdPrefix={`${storageIdPrefix}.timeline`}
                  component='patientTimeline'
                  config={config}
                  filter={filters.timeline}
                  setFilter={(newFilter) => {
                    changeFilters({timeline: newFilter});
                    setHealthcareDefaultScroll(0);
                  }}
                />
              </IconHeaderWithSearch>
              <Collapse in={filters.timelineOpen} timeout={500}>
                <div css={muiStyles.filterWrapper}>
                  <LightDateRangeFilter
                    config={{
                      name: 'dateRange',
                      ui: {
                        label: 'patient.detail.healthcare.filters.dateRange',
                      },
                    }}
                    value={
                      filters.timeline.startDate && filters.timeline.endDate
                        ? {
                            startDate: filters.timeline.startDate,
                            endDate: filters.timeline.endDate,
                            shortcut: filters.timeline.floatingShortcutName,
                          }
                        : null
                    }
                    onChange={(newValue) => {
                      changeFilters({
                        timeline: {
                          ...filters.timeline,
                          startDate: newValue?.startDate,
                          endDate: newValue?.endDate,
                          floatingShortcutName: newValue?.shortcut,
                        },
                      });
                      setHealthcareDefaultScroll(0);
                    }}
                    dateOnly
                    showShortcuts
                  />
                  <LightSelectFilter
                    config={{
                      name: 'eventTypes',
                      ui: {
                        label: 'patient.detail.healthcare.filters.eventType',
                      },
                    }}
                    options={[
                      {
                        id: 'HOSPITALIZATION',
                        label: t('patient.detail.healthcare.filters.eventType.hospitalization'),
                      },
                      {
                        id: 'OUTPATIENT',
                        label: t('patient.detail.healthcare.filters.eventType.outpatient'),
                      },
                      {
                        id: 'LABORATORY',
                        label: t('patient.detail.healthcare.filters.eventType.laboratory'),
                      },
                      {
                        id: 'HAI',
                        label: t('patient.detail.healthcare.filters.eventType.hai'),
                      },
                    ]}
                    value={filters.timeline.eventTypes}
                    onChange={(newValue) => {
                      changeFilters({
                        timeline: {
                          ...filters.timeline,
                          eventTypes: newValue,
                        },
                      });
                      setHealthcareDefaultScroll(0);
                    }}
                    multi
                  />
                </div>
              </Collapse>
              <DlMuiComponentBox noTopPadding>
                <div
                  className={classNames('dlLoadingSpinnerContainer', 'dlFlexBoxContainerInContainer', {
                    dlLoadingSpinnerParent: isLoading || isInitialLoad,
                  })}
                  data-name={'loadingSpinnerParent'}
                >
                  {isLoading || isInitialLoad ? (
                    <LoadingSpinner isVisible marginTop={0} />
                  ) : (
                    <LightTable
                      key={patientId}
                      columns={timelineColumns}
                      data={sortedEvents}
                      onOrderChange={(field, direction) => {
                        changeFilters({
                          timelineSorting: {field, direction},
                        });
                      }}
                      controlledSort={(() => {
                        return filters.timelineSorting;
                      })()}
                      cssVars={{
                        rowHeight: 40,
                      }}
                      defaultScroll={{
                        to: healthcareDefaultScroll,
                        dependOn: null,
                      }}
                      onScroll={healthcareOnScroll}
                      useDynamicRows
                      chunkSize={25}
                      defaultDynamicChunks={healthcareDefaultDynamicChunks}
                      dynamicChunksOnChange={healthcareDynamicChunksOnChange}
                    />
                  )}
                </div>
              </DlMuiComponentBox>
            </div>
          </div>
          <aside
            data-name='right-column'
            className={classNames({
              [stylesLayout.dlRightColumn]: true,
            })}
            style={{borderRadius: '16px'}}
          >
            <patientContainer.PatientInfo isLoading={isLoading} data={data?.patient} />
          </aside>
        </LightSplitLayout>
      </div>
    </div>
  );
};

function getTimelineColumns(
  formatters: Formatters,
  sortTimeDirection: 'asc' | 'desc',
  patientId: string | undefined,
  snomedRef: React.MutableRefObject<{
    snomedLabels: Record<string, string>;
    snomedLabelsLoading: boolean;
  }>,
  getTranslationForStandardTypeId?: (standardTypeId: number) => string,
  getCodeLabelForStandardTypeId?: (standardTypeId: number) => string
): LTColumn<RowType>[] {
  const {t, formatDate} = formatters;
  return [
    /* {
      field: 'number',
      title: '#',
      render: (_1, _2, index) => String(index),
    }, */
    {
      field: 'date',
      title: t('patient.detail.healthcare.date'),
      render: (rowData) => formatDate(rowData.timestamp ?? ''),
      sorting: true,
      width: '103px',
    },
    {
      field: 'eventType',
      title: t('patient.detail.healthcare.eventType'),
      minWidth: 170, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
      render: (rowData) => (
        <HealthcareTableEventLinkFormatter
          data={rowData}
          patientId={patientId}
          sortTimeDirection={sortTimeDirection}
          formatters={formatters}
          snomedLabels={snomedRef.current.snomedLabels}
          standardTypeCodeLabel={
            getCodeLabelForStandardTypeId && rowData.haiStandardTypeId
              ? getCodeLabelForStandardTypeId(rowData.haiStandardTypeId)
              : undefined
          }
          standardTypeTranslation={
            getTranslationForStandardTypeId && rowData.haiStandardTypeId
              ? getTranslationForStandardTypeId(rowData.haiStandardTypeId)
              : undefined
          }
        />
      ),
      // maxWidth: '250px',
    },
    {
      field: 'detail',
      title: t('patient.detail.healthcare.detail'),
      minWidth: 180, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
      render: (rowData) => (
        <HealthcareTableDetailFormatter
          {...AG_GRID_COMPATIBILITY}
          data={rowData}
          snomedLabels={snomedRef.current.snomedLabels}
          snomedLabelsLoading={snomedRef.current.snomedLabelsLoading}
        />
      ),
    },
  ];
}

const getStyles: GetStylesFunction = (options) => {
  return {
    wrapper: css`
      width: 100%;
      height: 100%;
      padding: 16px 16px 16px 0;
    `,
    leftColumn: css`
      display: flex;
      flex-direction: column;
    `,
    leftColumnBottom: css`
      background: white;
      padding: 16px;
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    `,
    filterWrapper: css`
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    `,
  };
};
